import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import * as _ from 'lodash';
import { __, dateFrom, openInNewTab } from '../core/utils';
import Dropdown from './Dropdown';
import { hideDropdown } from '../redux/actions/general/dropdown';
import { listAttachmentVersions } from '../redux/actions/attachments';
import MenuVIcon from '../assets/images/menu-v-15x15.svg';
import styles from '../styles/components/AttachmentFileMenu.scss';

function AttachmentFileMenu({
  name,
  row,
  canManageFile = true,
  canVersionFile = canManageFile,
  onDownloadFile,
  onOpenEdit,
  onOpenUploadVersion,
  onArchiveAttachment,
  onDeleteFile,
  onAddToFolders,
  wrapperClassName,
  iconClassName,
  compact = false,
}) {
  const [versionsPlacement, setVersionsPlacement] = useState('left');
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const attachmentId = row.attachment_id || row.id;
  const activeAttachmentId = useRef(attachmentId);

  useEffect(() => {
    activeAttachmentId.current = attachmentId;
    setShowVersions(false);
    setVersions(null);
    setVersionsLoading(false);

    return () => {
      if (activeAttachmentId.current == attachmentId) {
        activeAttachmentId.current = null;
      }
    };
  }, [
    attachmentId,
    row.attachment_version_id,
    row.current_version_created_at,
    row.version,
  ]);

  const canOpenInNewTab =
    row.stream_url &&
    row.stream_url.length > 0 &&
    _.includes(
      ['jpg', 'jpeg', 'png', 'pdf', 'html'],
      _.toLower(row.extension || row.ext),
    );

  const handleLoadVersions = (e) => {
    const menuWidth = 210;
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    setVersionsPlacement(
      spaceLeft < menuWidth && spaceRight > spaceLeft ? 'right' : 'left',
    );

    e.preventDefault();
    e.stopPropagation();
    setShowVersions(true);

    if (versions !== null || versionsLoading) {
      return;
    }

    setVersionsLoading(true);
    listAttachmentVersions(attachmentId).then(
      ({ response }) => {
        if (activeAttachmentId.current == attachmentId) {
          setVersions(_.values(response.data.versions));
          setVersionsLoading(false);
        }
      },
      () => {
        if (activeAttachmentId.current == attachmentId) {
          setVersionsLoading(false);
        }
      },
    );
  };

  const closeVersions = () => setShowVersions(false);

  return (
    <Dropdown
      portal
      closeOnScroll
      top={22}
      right={0}
      wrapperClassName={classNames(styles.fileMenuWrapper, wrapperClassName)}
      dropdownClassName={styles.fileMenuDropdown}
      name={name}
      header={
        <div className={styles.fileMenuHeader}>
          {__('attachments.menu.file')}
        </div>
      }
      content={
        <div
          className={styles.fileMenuContent}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className={styles.fileMenuItem}
            onClick={() => {
              closeVersions();
              hideDropdown();
              onDownloadFile(row, true);
            }}
          >
            {__('attachments.menu.download')}
          </div>
          <div
            className={classNames(
              styles.fileMenuItem,
              !canVersionFile && styles.disabled,
            )}
            onClick={() => {
              closeVersions();
              canVersionFile && onOpenEdit(row);
            }}
          >
            {__('attachments.menu.edit')}
          </div>
          <div
            className={classNames(
              styles.fileMenuItem,
              !canVersionFile && styles.disabled,
            )}
            onClick={() => {
              closeVersions();
              setVersions(null);
              canVersionFile && onOpenUploadVersion(row);
            }}
          >
            {__('attachments.menu.upload-new-version')}
          </div>
          <div
            className={classNames(
              styles.fileMenuItem,
              !canVersionFile && styles.disabled,
            )}
            onMouseEnter={(e) => canVersionFile && handleLoadVersions(e)}
            onMouseLeave={closeVersions}
            onClick={(e) => canVersionFile && handleLoadVersions(e)}
          >
            <span>{__('attachments.menu.versions')}</span>
            <strong>&gt;</strong>
            {canVersionFile && showVersions && (
              <div
                className={classNames(
                  styles.versionsMenu,
                  versionsPlacement == 'right' && styles.openRight,
                )}
              >
                {versionsLoading ? (
                  <div className={styles.versionItem}>
                    {__('general.loading')}
                  </div>
                ) : (
                  _.map(versions || [], (version) => (
                    <div
                      key={version.attachment_version_id}
                      className={classNames(
                        styles.versionItem,
                        version.current && styles.current,
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        closeVersions();
                        hideDropdown();
                        onDownloadFile(version, false);
                      }}
                    >
                      <span>
                        {__(
                          'attachments.version.label',
                          {
                            version: version.version,
                          },
                          'Version ' + version.version,
                        )}
                      </span>
                      <strong>{dateFrom(version.created_at)}</strong>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div
            className={classNames(
              styles.fileMenuItem,
              !canVersionFile && styles.disabled,
            )}
            onClick={() => {
              closeVersions();
              canVersionFile && onArchiveAttachment(row);
            }}
          >
            {row.archived
              ? __('attachments.menu.unarchive')
              : __('attachments.menu.archive')}
          </div>
          <div
            className={classNames(
              styles.fileMenuItem,
              !canManageFile && styles.disabled,
            )}
            onClick={() => {
              closeVersions();
              setVersions(null);
              canManageFile && onDeleteFile(row);
            }}
          >
            {__('attachments.menu.delete')}
          </div>
          {onAddToFolders && (
            <div
              className={styles.fileMenuItem}
              onClick={() => {
                closeVersions();
                hideDropdown();
                onAddToFolders(row);
              }}
            >
              {__('attachments.menu.add-to-folders', {}, 'Add to folders')}
            </div>
          )}
          <div
            className={classNames(
              styles.fileMenuItem,
              !canOpenInNewTab && styles.disabled,
            )}
            onClick={() => {
              closeVersions();
              hideDropdown();
              canOpenInNewTab && openInNewTab(row.stream_url);
            }}
          >
            {__('attachments.menu.open_in_new_tab', {}, 'Open in New Tab')}{' '}
          </div>
        </div>
      }
    >
      <MenuVIcon
        className={classNames(
          styles.fileMenuIcon,
          compact && styles.compact,
          iconClassName,
        )}
      />
    </Dropdown>
  );
}

export default AttachmentFileMenu;
